import { getUrlDomain } from "./get-domain";

const GOOGLE_MEET_DOMAIN = 'meet.google.com';
const GOOGLE_MEET_CODE_PATTERN = /[a-z0-9]{3}-[a-z0-9]{4}-[a-z0-9]{3}/i;

export const UrlUtilities = {

	getUrlDomain: (url: string | undefined): string => {
		return getUrlDomain(url);
	},

	getGoogleMeetLink: (googleMeetCode: string | undefined): string | undefined => {
		if (!googleMeetCode) return undefined;
		return `https://${GOOGLE_MEET_DOMAIN}/${googleMeetCode}`;
	},

	/**
	 * Extracts the Google Meet code from a given input string.
	 * The Google Meet code typically follows a 3-4-3 pattern (e.g., abc-defg-hij).
	 * The function is designed to handle various input formats, including:
	 * - Full Google Meet URLs (e.g., https://meet.google.com/abc-defg-hij)
	 * - Just the code itself (e.g., abc-defg-hij)
	 * - URLs with additional query parameters (e.g., https://meet.google.com/abc-defg-hij?authuser=0)
	 * The function will return the extracted code in lowercase, or undefined if no valid code is found.
	 * 
	 * @param input - The input string that may contain the Google Meet code.
	 * @returns The extracted Google Meet code in lowercase, or undefined if not found.
	 */
	extractMeetCode(input: string | undefined): string | undefined {
		const trimmedInput = input?.trim();

		if (!trimmedInput) return undefined;

		//Clean the string and remove query params
		const cleanInput = trimmedInput.split('?')[0] ?? '';

		//Look for the 3-4-3 pattern (abc-defg-hij)
		const pattern = /[a-z0-9]{3}-[a-z0-9]{4}-[a-z0-9]{3}/i;
		const match = cleanInput.match(GOOGLE_MEET_CODE_PATTERN);

		if (match) {
			return match[0].toLowerCase();
		}

		// Last-resort fallback: If it's a google meet URL but doesn't follow 3-4-3 
		// (e.g. Google changes the format), take the last segment of the path.
		// check for url and contains
		if (getUrlDomain(cleanInput) === GOOGLE_MEET_DOMAIN) {
			const segments = cleanInput.split('/');
			const lastSegment = segments[segments.length - 1];

			// Return if it looks like a code (not empty, not a known page like 'landing' or 'lookup')
			return (lastSegment && !['landing', 'lookup'].includes(lastSegment))
				? lastSegment.toLowerCase()
				: undefined;
		}

		return undefined;
	},

	/**
	 * Converts a string into a URL-friendly slug.
	 * - Trims whitespace
	 * - Normalizes diacritics (ắ -> a)
	 * - Removes non-alphanumeric characters
	 * - Collapses multiple spaces into a single underscore
	 * 
	 * @example
	 *	const input = "  Nguyễn   Quốc  ắ !! ";
	 *	const slug = slugify(input);
	 *	console.log(slug); // Output: "nguyen_quoc_a"
	 */
	slugify: (text: string) => {
		return text
			.toString()						// Ensure input is a string
			.trim()							// Remove leading/trailing whitespace
			.toLowerCase()					// Convert to lowercase
			.normalize('NFD')				// Decompose combined characters (e.g., ắ to a + accents)
			.replace(/[\u0300-\u036f]/g, '')// Remove the separated accent marks
			.replace(/[^a-z0-9\s]/g, '')	// Remove anything that isn't a letter, number, or space
			.replace(/\s+/g, '_');			// Replace sequences of whitespace with a single underscore
	},
};